import { useQueryParams } from '@hooks/queryParams';
import { formatDate } from '@utils/dateHelpers';
import {
  WorkloadDraftsItems,
  WorkloadMonthlyCalendarItems,
  WorkloadMonthlyCalendarRespSuccess,
} from './types';
import { ApiError } from '@root/globalTypes';
import { queryKeys } from '@utils/consts';
import { useQuery } from '@tanstack/react-query';
import { queryFetch } from '@services/api';

export const useGetWorkloadMonthlyCalendar = (date: Date) => {
  const { params, parseParams } = useQueryParams();

  const { type, search, unassigned } = params;
  const noOfWeeks = type === 'two-week' ? 2 : undefined;

  const queryParams = parseParams(
    {
      date: formatDate(date),
      noOfWeeks,
      search,
      unassigned,
    },
    { arrayFormat: 'bracket' },
  );

  const { data, error, isPending, isPlaceholderData, isFetching } = useQuery<
    WorkloadMonthlyCalendarRespSuccess,
    ApiError
  >({
    queryKey: noOfWeeks
      ? [...queryKeys.workloadTwoWeeklyCalendar, queryParams]
      : [...queryKeys.workloadMonthlyCalendar, queryParams],
    queryFn: async () => await queryFetch({ url: `workloads/monthly${queryParams}` }),
    placeholderData: (previousData) => previousData as WorkloadMonthlyCalendarRespSuccess,
    enabled: ['two-week', 'month'].includes(type || ''),
    select: (d) => {
      const parsedData: WorkloadMonthlyCalendarItems = {};
      const parsedDrafts: WorkloadDraftsItems = {};

      Object.entries(d.records).forEach(([key, records]) => {
        // eslint-disable-next-line immutable/no-mutation
        parsedData[key] = records;
      });
      Object.entries(d.drafts).forEach(([key, records]) => {
        // eslint-disable-next-line immutable/no-mutation
        parsedDrafts[key] = records;
      });

      return { ...d, data: parsedData, drafts: parsedDrafts };
    },
  });

  return {
    data,
    error,
    isLoading: isFetching && (isPending || isPlaceholderData),
  };
};
